import {
	type ReactNode,
	createContext,
	useContext,
	useEffect,
	useMemo,
} from "react";
import { AgentChatComposerDraftProvider } from "./agent-chat-composer-draft-context";
import { useAgentChat } from "./use-agent-chat";

export type AgentChatContextValue = ReturnType<typeof useAgentChat>;

export type AgentChatActionsContextValue = Pick<
	AgentChatContextValue,
	"startNewChat" | "loadChat"
>;

const AgentChatContext = createContext<AgentChatContextValue | null>(null);
const AgentChatActionsContext =
	createContext<AgentChatActionsContextValue | null>(null);

export function AgentChatProvider({ children }: { children: ReactNode }) {
	const chat = useAgentChat();
	const value = useMemo(() => chat, [chat]);
	const actionsValue = useMemo<AgentChatActionsContextValue>(
		() => ({
			startNewChat: chat.startNewChat,
			loadChat: chat.loadChat,
		}),
		[chat.startNewChat, chat.loadChat],
	);

	useEffect(() => {
		if (typeof window !== "undefined") {
			(
				window as unknown as {
					__AGENT_CHAT__?: AgentChatContextValue;
				}
			).__AGENT_CHAT__ = value;
		}
	}, [value]);

	return (
		<AgentChatActionsContext.Provider value={actionsValue}>
			<AgentChatContext.Provider value={value}>
				<AgentChatComposerDraftProvider>
					{children}
				</AgentChatComposerDraftProvider>
			</AgentChatContext.Provider>
		</AgentChatActionsContext.Provider>
	);
}

export function useAgentChatContext(): AgentChatContextValue {
	const context = useContext(AgentChatContext);
	if (!context) {
		throw new Error(
			"useAgentChatContext must be used within AgentChatProvider",
		);
	}
	return context;
}

export function useOptionalAgentChatContext(): AgentChatContextValue | null {
	return useContext(AgentChatContext);
}

export function useOptionalAgentChatActions(): AgentChatActionsContextValue | null {
	return useContext(AgentChatActionsContext);
}
